import { FC } from "react";
import { Product } from "@/types/product";
import styles from "./index.module.scss";
import { getTextFromTranslations } from "@/helpers/translation";
import { formatMoney } from "@/helpers/numbers";
import SearchIcon from "@/assets/images/SearchIcon";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { getPriceFromProduct } from "@/helpers/products";
import { getImageSrc } from "@/helpers/images";
import { useIsTakeOut } from "@/stores/cash-register";
import { useAppSelector } from "@/hooks/useAppSelector";
import { selectProductById } from "@/store/product/selectors";

interface ProductCardProps {
  productId?: Product["id"];
  onClick?: (product?: Product) => void;
  hidePrice?: boolean;
  className?: string;
}

const ProductCard: FC<ProductCardProps> = ({
  productId,
  onClick,
  hidePrice = false,
  className,
}) => {
  const product = useAppSelector((state) => selectProductById(state, productId));
  const name = getTextFromTranslations(product?.translations)?.name;
  if (!productId) return <SearchProductCard onClick={onClick} className={className} />;
  if (!product) return null;
  return (
    <div
      className={classNames(styles.container, className)}
      onClick={() => onClick?.(product)}
      data-cy="product-card"
      data-product-id={product.id}>
      {product.requiredAge && (
        <div className={styles.ageCheck}>
          <p className={styles.ageCheckText}>{product.requiredAge}+</p>
        </div>
      )}
      <div className={styles.imageWrapper}>
        <img src={getImageSrc(product)} alt={name} />
        {!hidePrice && (
          <p className={styles.priceLabel} data-cy="product-price-label">
            {formatMoney(getPriceFromProduct(product) / 100)}
          </p>
        )}
      </div>
      <div data-cy="card-product-name" className={styles.productNameWrapper}>
        <p className={styles.title}>{name}</p>
      </div>
    </div>
  );
};

const SearchProductCard: FC<Omit<ProductCardProps, "product">> = ({ onClick, className }) => {
  const { t } = useTranslation();
  const isTakeOut = useIsTakeOut();

  return (
    <div
      onClick={() => onClick?.()}
      className={classNames(styles.container, styles.searchProductContainer, className)}
      data-cy="search-product-card">
      <SearchIcon />
      <div>
        <p className={classNames(styles.title, "h6")}>{t("components.searchProduct")}</p>
        {isTakeOut && <p className={styles.helperText}>({t("components.withoutBarCode")})</p>}
      </div>
    </div>
  );
};

export { ProductCard };
